import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import {
  MapContainerBox,
  MapCoordinatesText,
  MapInfoBar,
  MapRemovePinButton,
  MapSearchInput,
  MapSection,
  MapSectionTitle,
} from './modal.styles';
import { GOOGLE_MAPS_LIBRARIES } from '../../GeofenceV2/map/map';
import { createLocationPinIcon } from '../helper';

const isSamePoint = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  // Account for floating point inaccuracies common in geo calculations
  return Math.abs(a.lat - b.lat) < 0.0000001 && Math.abs(a.lng - b.lng) < 0.0000001;
};

const PinDropPremiseMap = ({ value, onChange, isEdit }) => {
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const markerRef = useRef(null);

  const [position, setPosition] = useState(value || null);
  const [mapCenter, setMapCenter] = useState(value || null);
  const [showBlockedHint, setShowBlockedHint] = useState(false);

  // CRITICAL FIX: Explicit state to track when the map instance is hot and ready in the DOM
  const [mapInstance, setMapInstance] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyB1KCluOXIh5ipV4wcHe3z-ottrOfel1As',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Sync state cleanly if data changes via parent context
  useEffect(() => {
    if (!isSamePoint(value, position)) {
      const nextPos = value || null;
      setPosition(nextPos);
      if (nextPos) {
        setMapCenter(nextPos);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Resolve browser location once if initial fallback is required
  useEffect(() => {
    if (mapCenter) return;

    if (!navigator.geolocation) {
      alert('Location access is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMapCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        alert('Please allow location access to show your current location on the map.');
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emitChange = (next) => {
    setPosition(next);
    onChange?.(next);
  };

  const handleMapClick = useCallback(
    (e) => {
      if (position) {
        setShowBlockedHint(true);
        setTimeout(() => setShowBlockedHint(false), 2500);
        return;
      }
      emitChange({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    },
    [position] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleRemovePin = () => {
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    emitChange(null);
  };

  const handleLoadMap = (map) => {
    mapRef.current = map;
    setMapInstance(map); // Triggers marker effect hook with verified canvas reference

    setTimeout(() => {
      if (!mapRef.current || !window.google?.maps?.event) return;
      window.google.maps.event.trigger(mapRef.current, 'resize');
      mapRef.current.setCenter(mapRef.current.getCenter());
    }, 300);
  };

  const handleLoadAutocomplete = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    const geometry = place?.geometry;
    if (!geometry) return;

    if (geometry.viewport) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.union(geometry.viewport);
      mapRef.current?.fitBounds(bounds);
    } else if (geometry.location) {
      mapRef.current?.panTo(geometry.location);
      mapRef.current?.setZoom(15);
    }
  };

  // ---------------------------------------------------------------------
  // THE LIFECYCLE REPAIR: Sync pin presence without recreation cycles
  // ---------------------------------------------------------------------
  useEffect(() => {
    // Adding mapInstance to the dependencies ensures marker placement runs as soon as map becomes ready
    if (!isLoaded || !mapInstance || !window.google?.maps) return;

    // Handle pin deletion safely
    if (!position) {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      return;
    }

    // If marker exists, update location smoothly instead of dropping/recreating the pin
    if (markerRef.current) {
      const currentMarkerPos = markerRef.current.getPosition();
      const currentNativeCoords = { lat: currentMarkerPos.lat(), lng: currentMarkerPos.lng() };

      if (!isSamePoint(position, currentNativeCoords)) {
        markerRef.current.setPosition(position);
      }
      return;
    }

    // Instantiate marker cleanly if missing
    const marker = new window.google.maps.Marker({
      position,
      map: mapInstance,
      draggable: true,
      icon: createLocationPinIcon(window.google),
    });

    const dragListener = marker.addListener('dragend', (e) => {
      const nextCoords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      emitChange(nextCoords);
    });

    markerRef.current = marker;

    return () => {
      window.google.maps.event.removeListener(dragListener);
      if (markerRef.current === marker) {
        marker.setMap(null);
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, mapInstance, position?.lat, position?.lng]);

  if (!isLoaded) {
    return (
      <MapSection>
        <MapSectionTitle>Master Centroid</MapSectionTitle>
        <MapContainerBox style={{ display: 'grid', placeItems: 'center', color: '#fff' }}>
          Loading map...
        </MapContainerBox>
      </MapSection>
    );
  }

  const hasKnownCenter = Boolean(mapCenter);

  return (
    <MapSection>
      <MapSectionTitle>Master Centroid</MapSectionTitle>

      <Autocomplete onLoad={handleLoadAutocomplete} onPlaceChanged={handlePlaceChanged}>
        <MapSearchInput type="text" placeholder="Search a location..." />
      </Autocomplete>

      <MapContainerBox>
        <GoogleMap
          zoom={hasKnownCenter ? 16 : 6}
          center={mapCenter || { lat: 0, lng: 0 }}
          onLoad={handleLoadMap}
          onClick={handleMapClick}
          mapContainerStyle={{ width: '100%', height: '100%' }}
          options={{
            zoomControl: true,
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: true,
            mapTypeControlOptions: {
              style: window.google?.maps?.MapTypeControlStyle?.HORIZONTAL_BAR,
              position: window.google?.maps?.ControlPosition?.TOP_RIGHT,
              mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain'],
            },
            draggableCursor: position ? 'default' : 'crosshair',
          }}
        />
      </MapContainerBox>

      <MapInfoBar>
        <MapCoordinatesText>
          {position
            ? `Lat: ${position.lat.toFixed(6)}, Lng: ${position.lng.toFixed(6)}`
            : 'Click the map to drop a pin.'}
        </MapCoordinatesText>
        {!isEdit && (
          <MapRemovePinButton type="button" onClick={handleRemovePin} disabled={!position}>
            Remove pin
          </MapRemovePinButton>
        )}
      </MapInfoBar>

      {showBlockedHint && (
        <MapCoordinatesText style={{ color: '#d9534f', marginTop: '4px' }}>
          {isEdit
            ? 'The master centroid cannot be changed once it has been set.'
            : 'Only one pin is allowed — remove the current pin before adding a new one.'}
        </MapCoordinatesText>
      )}
    </MapSection>
  );
};

export default PinDropPremiseMap;
